import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from '../util/amplifyAuth';
import { getSubscriptionStatus, getOrders, PLAN_NAMES } from '../components/API';
import UserHeader from '../components/UserHeader/UserHeader';
import '../styles/Pricing.css';

function Billing() {
    const navigate = useNavigate();
    const [authChecked, setAuthChecked] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAuthSession()
            .then(() => {
                setAuthChecked(true);
                return Promise.all([
                    getSubscriptionStatus().catch(() => null),
                    getOrders().catch(() => ({ orders: [] }))
                ]);
            })
            .then(([sub, orderData]) => {
                if (sub?.subscription) setSubscription(sub.subscription);
                if (orderData?.orders) setOrders(orderData.orders);
                setLoading(false);
            })
            .catch(() => navigate('/', { replace: true }));
    }, [navigate]);

    // Subscription management (plan changes, cancel, resume) lives in Settings →
    // Subscription; the Stripe portal only handles payment methods + invoices now.
    const handleManage = () => {
        navigate('/settings?section=subscription');
    };

    if (!authChecked) return null;

    const statusLabel = {
        active: 'Active',
        trialing: 'Trial',
        past_due: 'Past Due',
        canceled: 'Canceled',
        unpaid: 'Unpaid'
    };

    const statusClass = {
        active: 'status-active',
        trialing: 'status-active',
        past_due: 'status-warn',
        canceled: 'status-canceled',
        unpaid: 'status-warn'
    };

    return (
        <div className="pricing-container">
            <UserHeader />
            <div className="pricing-content">
                <button className="pricing-back" onClick={() => navigate('/home')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" />
                        <path d="M12 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </button>

                <h1 className="pricing-title">Billing</h1>

                {loading ? (
                    <div className="billing-loading">Loading...</div>
                ) : (
                    <>
                        <div className="billing-section">
                            <h2 className="billing-section-title">Subscription</h2>
                            {subscription && subscription.status ? (
                                <div className="billing-card">
                                    <div className="billing-row">
                                        <span className="billing-label">Status</span>
                                        <span className={`billing-status ${statusClass[subscription.status] || ''}`}>
                                            {statusLabel[subscription.status] || subscription.status}
                                        </span>
                                    </div>
                                    {subscription.plan && (
                                        <div className="billing-row">
                                            <span className="billing-label">Plan</span>
                                            <span className="billing-value">{PLAN_NAMES[subscription.plan] || subscription.plan}</span>
                                        </div>
                                    )}
                                    {subscription.currentPeriodEnd && (
                                        <div className="billing-row">
                                            <span className="billing-label">
                                                {subscription.status === 'trialing' ? 'Trial ends' : 'Renews'}
                                            </span>
                                            <span className="billing-value">
                                                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                    <button
                                        className="billing-manage-btn"
                                        onClick={handleManage}
                                    >
                                        Manage Subscription
                                    </button>
                                </div>
                            ) : (
                                <div className="billing-card billing-empty">
                                    <p>No active subscription.</p>
                                    <button className="pricing-cta" onClick={() => navigate('/pricing')}>
                                        View Plans
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="billing-section">
                            <h2 className="billing-section-title">Order History</h2>
                            {orders.length > 0 ? (
                                <div className="billing-orders">
                                    {orders.map(order => (
                                        <div key={order.orderId} className="billing-order-row">
                                            <div className="billing-order-info">
                                                <span className="billing-order-type">{order.description || order.type}</span>
                                                <span className="billing-order-date">
                                                    {new Date(order.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="billing-order-right">
                                                <span className="billing-order-amount">
                                                    ${(order.amountTotal / 100).toFixed(2)}
                                                </span>
                                                <span className={`billing-order-status ${order.status === 'completed' ? 'status-active' : ''}`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="billing-card billing-empty">
                                    <p>No orders yet.</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Billing;
